import { useIntl } from "react-intl";
import React, { Fragment, useState } from "react";
import cn from "classnames";

// Mui Components
import { TableRow, TableCell, IconButton, Collapse, Box, Typography, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

// Enums
import icons from "enums/icons";

// Styles
import useUsersTableStyles from "./UsersTable.styles";

// Utils
import { format } from "date-fns";
import { ConvertedUsersListData } from "../adapters/converters";

//  Components
import { TagName } from "components/TagName";
import CustomIcon from "components/CustomIcon";
import { InfoWithIcon } from "pages/ClientHub/components";

const UsersTableRow = ({
    data,
    selectedUsers,
    Checkbox,
    RemoveUserButton,
    WHButton,
    AddUserButton,
    width,
}: {
    data: ConvertedUsersListData;
    selectedUsers: ConvertedUsersListData[];
    Checkbox?: React.ReactNode;
    RemoveUserButton?: React.ReactNode;
    WHButton?: React.ReactNode;
    AddUserButton?: React.ReactNode;
    width?: number;
}) => {
    const intl = useIntl();
    const classes = useUsersTableStyles();
    const [shouldExpand, setShouldExpand] = useState(false);

    const selectedUsersId = selectedUsers.map((user) => user.id);

    const hasLinkedin = Boolean(data.linkedinId);

    const [showHiddenHubs, setShowHiddenHubs] = useState(false);

    const handleToggleExpand = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        setShouldExpand(!shouldExpand);
    };

    const handleLinkedinClick = (ev: React.SyntheticEvent) => {
        ev.stopPropagation();
    };

    const handleShowHubs = (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        setShowHiddenHubs(!showHiddenHubs);
    };

    return (
        <Fragment>
            <TableRow
                className={cn({ [classes.selectedRow]: selectedUsersId.includes(data.id) })}
                onClick={(event) => handleToggleExpand(event)}
            >
                <TableCell padding="checkbox">{Checkbox}</TableCell>
                <TableCell>
                    <InfoWithIcon
                        startSlot={
                            <IconButton onClick={(event) => handleToggleExpand(event)}>
                                <InfoIcon
                                    sx={(theme) => ({
                                        fontSize: 24,
                                        color: theme.palette.info.main,
                                    })}
                                />
                            </IconButton>
                        }
                        entityName={data.name}
                        primary={data.company}
                        secondary={data.position}
                        width={width}
                    />
                </TableCell>
                <TableCell>{data.company}</TableCell>
                <TableCell>{data.activeOn !== null ? format(new Date(data.activeOn), "dd/MM/yyyy") : ""}</TableCell>
                <TableCell>
                    <Box display="flex" flexDirection="column">
                        {/* First HUB and +N button */}
                        {data.hubs && data.hubs.length > 0 && (
                            <Box className={classes.tagsBox}>
                                <Box>
                                    <TagName isTruncated={true}>{data.hubs[0].name}</TagName>
                                </Box>

                                {data.hubs.length > 1 && (
                                    <Tooltip
                                        arrow
                                        placement="bottom-start"
                                        title={data.hubs.slice(1).map((hub) => (
                                            <Box key={hub.id} component="li" sx={{ listStyleType: "none" }}>
                                                {hub.name}
                                            </Box>
                                        ))}
                                    >
                                        <Box
                                            className={cn(classes.showHubsButton, {
                                                [classes.activeButton]: showHiddenHubs,
                                            })}
                                            onClick={handleShowHubs}
                                        >
                                            + {data.hubs.length - 1}
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                        {/* Hidden HUBS */}
                        {showHiddenHubs && (
                            <Collapse in={showHiddenHubs}>
                                <Box className={classes.tagsBox} flexWrap="wrap" gap="8px" marginTop="8px">
                                    {data.hubs.slice(1).map((hub) => (
                                        <TagName key={hub.id} isTruncated={true}>
                                            {hub.name}
                                        </TagName>
                                    ))}
                                </Box>
                            </Collapse>
                        )}
                    </Box>
                </TableCell>
                {RemoveUserButton && <TableCell align="right">{RemoveUserButton}</TableCell>}
                {AddUserButton && <TableCell align="right">{AddUserButton}</TableCell>}
            </TableRow>
            <TableRow className={classes.expandableRow}>
                <TableCell colSpan={6}>
                    <Collapse in={shouldExpand} timeout="auto" unmountOnExit>
                        <Box display="flex" alignItems="center" py={1.2} px={0}>
                            <Box display="flex" alignItems="center" mr={2}>
                                {WHButton && WHButton}
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark", p: 0 }}
                                    aria-label="linkedin button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!hasLinkedin}
                                    href={hasLinkedin ? `https://www.linkedin.com/in/${data.linkedinId}/` : "#"}
                                    onClick={handleLinkedinClick}
                                >
                                    <CustomIcon icon={icons.linkedin} sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Box>
                            {data.email && (
                                <Box display="flex" alignItems="center" mr={2} color="text.secondary">
                                    <Typography mr={1}>
                                        {intl.formatMessage({ id: "hubManagement.client.userTable.email" })}:
                                    </Typography>
                                    <Typography>{data.email}</Typography>
                                </Box>
                            )}
                            {hasLinkedin && (
                                <Box display="flex" alignItems="center" color="text.secondary">
                                    <Typography mr={1}>
                                        {intl.formatMessage({ id: "hubManagement.client.userTable.linkedInID" })}:
                                    </Typography>
                                    <Typography>{data.linkedinId}</Typography>
                                </Box>
                            )}

                            <IconButton
                                className={classes.toggleCloseBtn}
                                onClick={(event) => handleToggleExpand(event)}
                            >
                                <CustomIcon icon={icons.close} />
                            </IconButton>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
};

export default UsersTableRow;
